import {type PaymentMethodCreateParams} from '@stripe/stripe-js';
import {type WCShippingAddress, type WCBillingAddress} from '../../../@type/woocommerce/order';
import {DispatchActionType} from '../models/DispatchActionType';
import {type IPeachPayCustomer} from '../../../@type/peachpay/customer';
import {type DispatchAction, store} from '../store';
import {createDispatchUpdate} from './initialState';

export function peachPayCustomerReducer(state: IPeachPayCustomer, action: DispatchAction<unknown>): IPeachPayCustomer {
	switch (action.type) {
		case DispatchActionType.PEACHPAY_CUSTOMER:
			return {
				...state,
				...(action as DispatchAction<IPeachPayCustomer>).payload,
			};
		case DispatchActionType.PEACHPAY_CUSTOMER_FIELDS:
			return {
				...state,
				form_fields: {
					...(action as DispatchAction<IPeachPayCustomer['form_fields']>).payload,
				},
			};
		case DispatchActionType.PEACHPAY_CUSTOMER_STRIPE_ID:
			return {
				...state,
				stripe_customer_id: (action as DispatchAction<string>).payload,
			};
		case DispatchActionType.PEACHPAY_CUSTOMER_SET_PREFERRED_PAYMENT_METHOD: {
			return {
				...state,
				preferred_payment_method: (action as DispatchAction<string>).payload,
			};
		}

		case DispatchActionType.PEACHPAY_CUSTOMER_SHIPPING: {
			if (state.form_fields['ship_to_different_address'] === '1') {
				return {
					...state,
					form_fields: {
						...state.form_fields,
						shipping_country: (action as DispatchAction<IShortAddress>).payload.country,
						shipping_state: (action as DispatchAction<IShortAddress>).payload.state,
						shipping_city: (action as DispatchAction<IShortAddress>).payload.city,
						shipping_postcode: (action as DispatchAction<IShortAddress>).payload.postcode,
					},
				};
			}

			return {
				...state,
				form_fields: {
					...state.form_fields,
					billing_country: (action as DispatchAction<IShortAddress>).payload.country,
					billing_state: (action as DispatchAction<IShortAddress>).payload.state,
					billing_city: (action as DispatchAction<IShortAddress>).payload.city,
					billing_postcode: (action as DispatchAction<IShortAddress>).payload.postcode,
				},
			};
		}

		default:
			// This is required to prevent a compile error with TSC and webpack.
			return {...state};
	}
}

// =========================== Action Creators =========================

export const updateCustomerStripeId = createDispatchUpdate<string>(DispatchActionType.PEACHPAY_CUSTOMER_STRIPE_ID);
export const updateCustomerFields = createDispatchUpdate<IPeachPayCustomer['form_fields']>(DispatchActionType.PEACHPAY_CUSTOMER_FIELDS);
export const updateCustomer = createDispatchUpdate<IPeachPayCustomer>(DispatchActionType.PEACHPAY_CUSTOMER);

export const updateCustomerShippingShortAddress = createDispatchUpdate<IShortAddress>(DispatchActionType.PEACHPAY_CUSTOMER_SHIPPING);

export const updateCustomerPreferredGateway = createDispatchUpdate<string>(DispatchActionType.PEACHPAY_CUSTOMER_SET_PREFERRED_PAYMENT_METHOD);

// =========================== Store Selectors =========================
function getShippingField(field: string) {
	const openShippingDetails = store.getState().peachPayCustomer.form_fields?.['ship_to_different_address'];

	return store.getState().peachPayCustomer.form_fields?.[`${(openShippingDetails === '1' ? 'shipping' : 'billing')}_${field}`] ?? '';
}

/**
 * Always returns the most up to date customer state
 */
export const PeachPayCustomer = {
	data: () => store.getState().peachPayCustomer,
	billing: {
		email: () => store.getState().peachPayCustomer.form_fields['billing_email'] ?? '',
		fullName: () => (store.getState().peachPayCustomer.form_fields['billing_first_name'] ?? '') + ' ' + (store.getState().peachPayCustomer.form_fields['billing_last_name'] ?? ''),
		firstName: () => store.getState().peachPayCustomer.form_fields['billing_first_name'] ?? '',
		lastName: () => store.getState().peachPayCustomer.form_fields['billing_last_name'] ?? '',
		phone: () => store.getState().peachPayCustomer.form_fields['billing_phone'] ?? '',
		company: () => store.getState().peachPayCustomer.form_fields['billing_company'] ?? '',
		address1: () => store.getState().peachPayCustomer.form_fields['billing_address_1'] ?? '',
		address2: () => store.getState().peachPayCustomer.form_fields['billing_address_2'] ?? '',
		city: () => store.getState().peachPayCustomer.form_fields['billing_city'] ?? '',
		state: () => store.getState().peachPayCustomer.form_fields['billing_state'] ?? '',
		country() {
			const location_data = store.getState().merchantConfiguration.general.wcLocationInfoData;
			// Because empty strings should be ignored as well
			// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
			return store.getState().peachPayCustomer.form_fields['billing_country'] || location_data?.customer_default_country || location_data?.store_country || '';
		},
		postal: () => store.getState().peachPayCustomer.form_fields['billing_postcode'] ?? '',
	},
	shipping: {
		email: () => getShippingField('email'),
		fullName: () => getShippingField('first_name') + ' ' + getShippingField('first_name'),
		firstName: () => getShippingField('first_name'),
		lastName: () => getShippingField('last_name'),
		phone: () => getShippingField('phone'),
		company: () => getShippingField('company'),
		address1: () => getShippingField('address_1'),
		address2: () => getShippingField('address_2'),
		city: () => getShippingField('city'),
		state: () => getShippingField('state'),
		country: () => getShippingField('country'),
		postal: () => getShippingField('postcode'),
	},
	shipToDifferentAddress: () => store.getState().peachPayCustomer.form_fields['ship_to_different_address'] === '1',
	stripeId: () => store.getState().peachPayCustomer.stripe_customer_id ?? '',
	stripeBillingDetails() {
		const details = {
			name: PeachPayCustomer.billing.firstName() + ' ' + PeachPayCustomer.billing.lastName(),
			email: PeachPayCustomer.billing.email(),
			phone: PeachPayCustomer.billing.phone(),
			address: {
				city: PeachPayCustomer.billing.city(),
				country: PeachPayCustomer.billing.country(),
				line1: PeachPayCustomer.billing.address1(),
				line2: PeachPayCustomer.billing.address2(),
				postal_code: PeachPayCustomer.billing.postal(),
				state: PeachPayCustomer.billing.state(),
			},
		} as PaymentMethodCreateParams.BillingDetails;

		if (details.name === ' ') {
			delete details.name;
		}

		if (details.email === '') {
			delete details.email;
		}

		if (details.phone === '') {
			delete details.phone;
		}

		if (details.address?.city === '') {
			delete details.address.city;
		}

		if (details.address?.country === '') {
			delete details.address.country;
		}

		if (details.address?.line1 === '') {
			delete details.address.line1;
		}

		if (details.address?.line2 === '') {
			delete details.address.line2;
		}

		if (details.address?.postal_code === '') {
			delete details.address.postal_code;
		}

		if (details.address?.state === '') {
			delete details.address.state;
		}

		if (Object.keys(details.address ?? {}).length === 0) {
			delete details.address;
		}

		return details;
	},
	stripeShippingDetails: () => ({
		name: PeachPayCustomer.shipping.firstName() + ' ' + PeachPayCustomer.shipping.lastName(),
		phone: PeachPayCustomer.shipping.phone(),
		address: {
			city: PeachPayCustomer.shipping.city(),
			country: PeachPayCustomer.shipping.country(),
			line1: PeachPayCustomer.shipping.address1(),
			line2: PeachPayCustomer.shipping.address2(),
			postal_code: PeachPayCustomer.shipping.postal(),
			state: PeachPayCustomer.shipping.state(),
		},
	}),
	shippingShortAddress: () => ({
		country: PeachPayCustomer.shipping.country(),
		state: PeachPayCustomer.shipping.state(),
		city: PeachPayCustomer.shipping.city(),
		postcode: PeachPayCustomer.shipping.postal(),
	} as IShortAddress),
	wcShippingAddress: () => ({
		shipping_email: getShippingField('email'),
		shipping_first_name: getShippingField('first_name'),
		shipping_last_name: getShippingField('last_name'),
		shipping_company: getShippingField('company'),
		shipping_country: getShippingField('country'),
		shipping_address_1: getShippingField('address_1'),
		shipping_address_2: getShippingField('address_2'),
		shipping_city: getShippingField('city'),
		shipping_state: getShippingField('state'),
		shipping_postcode: getShippingField('postcode'),
		shipping_phone: getShippingField('phone'),
	} as WCShippingAddress),
	wcBillingAddress: () => ({
		billing_first_name: PeachPayCustomer.billing.firstName(),
		billing_last_name: PeachPayCustomer.billing.lastName(),
		billing_company: PeachPayCustomer.billing.company(),
		billing_email: PeachPayCustomer.billing.email(),
		billing_phone: PeachPayCustomer.billing.phone(),
		billing_country: PeachPayCustomer.billing.country(),
		billing_address_1: PeachPayCustomer.billing.address1(),
		billing_address_2: PeachPayCustomer.billing.address2(),
		billing_city: PeachPayCustomer.billing.city(),
		billing_state: PeachPayCustomer.billing.state(),
		billing_postcode: PeachPayCustomer.billing.postal(),
	} as WCBillingAddress),
	preferredPaymentMethod: () => store.getState().peachPayCustomer.preferred_payment_method ?? null as string | null,
};

export interface IShortAddress {
	country: string;
	state: string;
	city: string;
	postcode: string;
}
